import { msg, t } from '@lingui/core/macro';

import type { SolErrorDetails } from '@/api/solType';

import { type ErrorByCodeType } from './types';

export enum CORE_ERROR_CODES {
  UNKNOWN = 'UNKNOWN_SOL_ERROR',
  NOT_FOUND = 'err:sol.data.not-found',
  UPDATE_FAILURE = 'err:sol.data.update-failure',
  FIELD_DOES_NOT_EXIST = 'err:sol.data.field-does-not-exist',
  MISSING_REQUIRED_FIELD = 'err:sol.data.missing-required-field',
  TOO_LONG = 'err:sol.data.too-long',
  NON_UNIQUE_ID = 'err:sol.core.nonUniqueId',
  OBJECT_IN_USE = 'err:sol.object-in-use',
  CANNOT_DELETE = 'err:sol.cannot-delete',

  ISE = 'err:core.ise',
  NO_INSTANCE = 'err:sol.core.no-instance',
  INSTANCE_ACCESS = 'err:sol.core.instance-access',
  RESPONSE_TOO_LARGE = 'err:sol.core.response-too-large',
  REQUEST_TOO_LARGE = 'err:sol.core.request-too-large',
  TOO_MANY_ERRORS = 'err:sol.core.too-many-errors',
  BAD_ID_PREFIX = 'err:sol.core.bad-id-prefix',
  MISSING_AUTHENTICATION = 'err:sol.core.missing-authentication',
  TIMEOUT = 'err:sol.core.timeout',
  OPERATION_LOCKED = 'err:sol.core.operationLocked',
  PERMISSION_DENIED = 'err:sol.core.permission-denied',
}

export const INTERNAL_CORE_ERROR = msg`A system error occurred. Please contact support for assistance.`;

export const CORE_ERROR_MESSAGES_BY_CODE: ErrorByCodeType = {
  [CORE_ERROR_CODES.UNKNOWN]: INTERNAL_CORE_ERROR,
  [CORE_ERROR_CODES.NOT_FOUND]: INTERNAL_CORE_ERROR,
  [CORE_ERROR_CODES.UPDATE_FAILURE]: msg`Unable to save changes. Please contact support for assistance.`,
  [CORE_ERROR_CODES.FIELD_DOES_NOT_EXIST]: INTERNAL_CORE_ERROR,
  [CORE_ERROR_CODES.MISSING_REQUIRED_FIELD]: msg`This field is required.`,
  [CORE_ERROR_CODES.TOO_LONG]: msg`The text supplied for the field is too long. Please shorten it and try again.`,
  [CORE_ERROR_CODES.NON_UNIQUE_ID]: INTERNAL_CORE_ERROR,
  [CORE_ERROR_CODES.OBJECT_IN_USE]: msg`The audience cannot be deleted. It is currently in use by: {objectNames}.
  {objectType, select, Audience {Update the rules of these Audiences before attempting to delete the object.}
                       Pixel {Update these Pixels in the Pixel Overview Screen before attempting to delete the object.}
                       Notification {Please have owners delete these notifications in their Profile pages.}
                       Instance {This object is being used in Company Settings. Please remove it from there before attempting to delete the object.}
                       other {Please remove from these objects before attempting to delete.}}`,
  [CORE_ERROR_CODES.CANNOT_DELETE]: msg`The object cannot be deleted. Please contact support for assistance.`,

  [CORE_ERROR_CODES.ISE]: INTERNAL_CORE_ERROR,
  [CORE_ERROR_CODES.NO_INSTANCE]: INTERNAL_CORE_ERROR,
  [CORE_ERROR_CODES.INSTANCE_ACCESS]: INTERNAL_CORE_ERROR,
  [CORE_ERROR_CODES.RESPONSE_TOO_LARGE]: INTERNAL_CORE_ERROR,
  [CORE_ERROR_CODES.REQUEST_TOO_LARGE]: INTERNAL_CORE_ERROR,
  [CORE_ERROR_CODES.TOO_MANY_ERRORS]: INTERNAL_CORE_ERROR,
  [CORE_ERROR_CODES.BAD_ID_PREFIX]: INTERNAL_CORE_ERROR,
  [CORE_ERROR_CODES.MISSING_AUTHENTICATION]: INTERNAL_CORE_ERROR,
  [CORE_ERROR_CODES.TIMEOUT]: INTERNAL_CORE_ERROR,
  [CORE_ERROR_CODES.OPERATION_LOCKED]: msg`Vendor Spend is currently being edited by {lockedBy} and can only be edited by one user at a time.`,
  [CORE_ERROR_CODES.PERMISSION_DENIED]: msg`You do not have permissions to perform this action. Access has been denied.`,
};

export const getObjectInUseDetails = (details: SolErrorDetails) => {
  if (details.objects && Array.isArray(details.objects)) {
    const maxDisplayed = 3;
    const displayedObjects = details.objects.slice(0, maxDisplayed);
    const remainingCount = details.objects.length - maxDisplayed;

    let objectNames = `"${displayedObjects.map((obj: { name: string }) => obj.name).join('", "')}"`;

    if (remainingCount > 0) {
      objectNames += t` and ${remainingCount} more`;
    }

    return {
      ...details,
      objectNames,
    };
  }

  return details;
};
